/** Europe/Athens helpers. Whole-day windows use local midnights, not a fixed 24-hour UTC span. */

export function timeZoneOffsetMs(utcInstant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(utcInstant);
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  let hour = Number(map.hour);
  let day = Number(map.day);
  if (hour === 24) {
    hour = 0;
    day += 1;
  }
  const asUtc = Date.UTC(Number(map.year), Number(map.month) - 1, day, hour, Number(map.minute), Number(map.second));
  return asUtc - utcInstant.getTime();
}

/** Convert a civil local date and time to a UTC instant. */
export function zonedLocalToUtc(isoDate: string, time: string, timeZone: string): Date {
  const [year, month, day] = isoDate.split("-").map(Number);
  const [hour, minute, second = 0] = time.split(":").map(Number);
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  let utc = wall;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const offset = timeZoneOffsetMs(new Date(utc), timeZone);
    const next = wall - offset;
    if (next === utc) break;
    utc = next;
  }
  return new Date(utc);
}

export function localDayBounds(isoDate: string, timeZone: string): { start: Date; end: Date } {
  const start = zonedLocalToUtc(isoDate, "00:00:00", timeZone);
  const [year, month, day] = isoDate.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  const nextIso = next.toISOString().slice(0, 10);
  const end = zonedLocalToUtc(nextIso, "00:00:00", timeZone);
  return { start, end };
}

export function weekdayOfCivilDate(isoDate: string): number {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

export function civilToday(timeZone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}

export function addDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

export function eachDate(from: string, toInclusive: string): string[] {
  const dates: string[] = [];
  let cursor = from;
  while (cursor <= toInclusive) {
    dates.push(cursor);
    cursor = addDays(cursor, 1);
    if (dates.length > 400) break;
  }
  return dates;
}

export function durationMilli(start: Date, end: Date): number {
  return Math.round((end.getTime() - start.getTime()) / 3_600);
}

export function occupancyWindow(serviceStart: Date, serviceEnd: Date, setupMinutes: number, teardownMinutes: number): { start: Date; end: Date } {
  return {
    start: new Date(serviceStart.getTime() - setupMinutes * 60_000),
    end: new Date(serviceEnd.getTime() + teardownMinutes * 60_000),
  };
}

export function intervalsOverlap(aStart: string | Date, aEnd: string | Date, bStart: string | Date, bEnd: string | Date): boolean {
  const a0 = typeof aStart === "string" ? Date.parse(aStart) : aStart.getTime();
  const a1 = typeof aEnd === "string" ? Date.parse(aEnd) : aEnd.getTime();
  const b0 = typeof bStart === "string" ? Date.parse(bStart) : bStart.getTime();
  const b1 = typeof bEnd === "string" ? Date.parse(bEnd) : bEnd.getTime();
  return a0 < b1 && b0 < a1;
}

export function iso(date: Date): string {
  return date.toISOString();
}
